import { io, Socket } from 'socket.io-client'
import type { MarketData } from '../../../shared/src/types'
import { logger } from '../utils/logger'
import { MarketHours } from '../utils/marketHours'

class WebSocketService {
  private socket: Socket | null = null
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectDelay = 1000
  private isConnecting = false
  private connectionStatusCallbacks: Array<(connected: boolean) => void> = []
  private marketDataCallbacks: Set<(data: MarketData) => void> = new Set()
  private subscribedSymbols: string[] = [] // Track subscribed symbols for reconnection
  private marketStatusCheckInterval: NodeJS.Timeout | null = null
  private wasMarketOpen = false

  constructor() {
    // Start monitoring market hours
    this.startMarketHoursMonitoring()
  }

  /**
   * Monitor market hours and connect/disconnect accordingly
   */
  private startMarketHoursMonitoring() {
    this.marketStatusCheckInterval = setInterval(() => {
      const isOpen = MarketHours.isMarketOpen()
      
      // Market just opened - connect and resubscribe
      if (isOpen && !this.wasMarketOpen) {
        console.log('📈 Market opened - connecting WebSocket')
        this.wasMarketOpen = true
        if (!this.socket?.connected) {
          this.connect()
        }
      }
      // Market just closed - disconnect to save resources
      else if (!isOpen && this.wasMarketOpen) {
        console.log('📉 Market closed - WebSocket will use REST fallback')
        this.wasMarketOpen = false
        // Don't disconnect, just let it fallback to REST
      }
    }, 30000) // Check every 30 seconds
    
    // Initial check
    this.wasMarketOpen = MarketHours.isMarketOpen()
  }

  connect(): void {
    // Prevent multiple simultaneous connection attempts
    if (this.socket?.connected || this.isConnecting) {
      return
    }
    
    // Check if market is open before connecting
    const isMarketOpen = MarketHours.isMarketOpen()
    if (!isMarketOpen) {
      console.log('📊 Market is closed - WebSocket will connect when market opens')
      return
    }

    this.isConnecting = true
    
    // Get auth token for WebSocket connection
    const token = localStorage.getItem('auth_token')
    
    const wsUrl = import.meta.env.VITE_WS_URL || 'http://localhost:3000'
    
    this.socket = io(wsUrl, {
      transports: ['websocket', 'polling'], // Allow fallback to polling
      upgrade: true,
      rememberUpgrade: true,
      autoConnect: true,
      timeout: 10000, // Increased timeout
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      withCredentials: true,
      auth: {
        token: token
      },
      extraHeaders: token ? {
        'Authorization': `Bearer ${token}`
      } : {}
    })

    this.socket.on('connect', () => {
      this.reconnectAttempts = 0
      this.isConnecting = false
      
      // Re-subscribe to market data if we have symbols (reconnection case)
      if (this.subscribedSymbols.length > 0) {
        this.socket?.emit('subscribe-market-data', this.subscribedSymbols)
      }
      
      // Call all connection status callbacks
      this.connectionStatusCallbacks.forEach(callback => {
        try {
          callback(true)
        } catch (error) {
          console.error('Error in connection status callback:', error)
        }
      })
    })

    this.socket.on('disconnect', (reason) => {
      this.isConnecting = false
      
      // Call all connection status callbacks
      this.connectionStatusCallbacks.forEach(callback => {
        try {
          callback(false)
        } catch (error) {
          console.error('Error in connection status callback:', error)
        }
      })
      
      // Only auto-reconnect for certain disconnect reasons
      if (reason === 'io server disconnect' || reason === 'transport close') {
        this.handleReconnect()
      }
    })

    this.socket.on('connect_error', (error) => {
      // Only log errors after multiple attempts (suppress initial connection handshake errors)
      if (this.reconnectAttempts > 2) {
        console.error('WebSocket connection error after multiple attempts:', error)
      }
      this.isConnecting = false
      this.handleReconnect()
    })
  }

  private handleReconnect(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++
      const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30000) // Max 30s delay
      
      setTimeout(() => {
        this.connect()
      }, delay)
    }
  }

  disconnect(): void {
    if (this.marketStatusCheckInterval) {
      clearInterval(this.marketStatusCheckInterval)
      this.marketStatusCheckInterval = null
    }
    
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
    }
  }

  // Market data subscriptions
  subscribeToMarketData(symbols: string[], callback: (data: MarketData) => void): void {
    // Store subscribed symbols for reconnection
    this.subscribedSymbols = symbols
    
    // Add callback to the set
    this.marketDataCallbacks.add(callback)
    
    // If market is open and we're not connected, connect now
    const isMarketOpen = MarketHours.isMarketOpen()
    if (isMarketOpen && !this.socket?.connected && !this.isConnecting) {
      console.log('📊 Market is open - connecting WebSocket for real-time data')
      this.connect()
      
      // Wait for connection before subscribing
      const onConnectionChange = (connected: boolean) => {
        if (connected) {
          this.sendSubscription(symbols)
          this.offConnectionStatusChange(onConnectionChange)
        }
      }
      this.onConnectionStatusChange(onConnectionChange)
      return
    }
    
    if (!this.socket?.connected) {
      if (isMarketOpen) {
        console.warn('📊 Market is open but WebSocket not connected - will use HTTP polling fallback')
      } else {
        console.log('📊 Market is closed - using HTTP polling')
      }
      return
    }
    
    this.sendSubscription(symbols)
  }
  
  private sendSubscription(symbols: string[]): void {
    if (!this.socket?.connected) return
    
    // Always ensure the listener is set up (in case socket was reconnected)
    // Remove old listener first to avoid duplicates
    this.socket.off('market-data-update')
    
    // Set up the listener
    let firstMessage = true
    this.socket.on('market-data-update', (data: MarketData) => {
      // Only log the first message to confirm it's working
      if (firstMessage) {
        console.log(`✅ WebSocket receiving live market data (broadcasting to ${this.marketDataCallbacks.size} callbacks)`)
        firstMessage = false
      }
      
      // Broadcast to all registered callbacks
      this.marketDataCallbacks.forEach(cb => {
        try {
          cb(data)
        } catch (error) {
          console.error('Error in market data callback:', error)
        }
      })
    })
    
    // Send subscription request
    console.log(`📡 Subscribing to ${symbols.length} symbols via WebSocket`)
    this.socket.emit('subscribe-market-data', symbols)
  }

  unsubscribeFromMarketData(symbols: string[], callback?: (data: MarketData) => void): void {
    if (!this.socket?.connected) return
    
    // Remove the specific callback if provided
    if (callback) {
      this.marketDataCallbacks.delete(callback)
    }
    
    // Send unsubscribe request
    this.socket.emit('unsubscribe-market-data', symbols)
    
    // If no more callbacks, remove the listener entirely and clear subscribed symbols
    if (this.marketDataCallbacks.size === 0) {
      this.socket.off('market-data-update')
      this.subscribedSymbols = []
    }
  }

  // Portfolio real-time updates
  joinPortfolioRoom(portfolioId: string, userId: string): void {
    if (!this.socket?.connected) {
      return
    }

    this.socket.emit('join-portfolio', { portfolioId, userId })
  }

  leavePortfolioRoom(portfolioId: string, userId: string): void {
    if (!this.socket?.connected) return

    this.socket.emit('leave-portfolio', { portfolioId, userId })
  }

  onPortfolioUpdate(callback: (data: any) => void): void {
    if (!this.socket) {
      console.warn('📡 WebSocket not connected, portfolio updates will be unavailable')
      return
    }

    logger.debug('Setting up portfolio update listener')
    this.socket.on('portfolio-update', callback)
  }

  onPortfolioJoined(callback: (data: { portfolioId: string, success: boolean, error?: string }) => void): void {
    if (!this.socket) return

    this.socket.on('portfolio-joined', callback)
  }

  offPortfolioUpdate(): void {
    if (!this.socket) return

    this.socket.off('portfolio-update')
    this.socket.off('portfolio-joined')
  }

  // Connection status
  isConnected(): boolean {
    return this.socket?.connected ?? false
  }

  onConnectionStatusChange(callback: (connected: boolean) => void): void {
    // Store the callback for future use
    this.connectionStatusCallbacks.push(callback)
    logger.debug('Connection status callback registered, total callbacks:', this.connectionStatusCallbacks.length)
    
    // If socket is already connected, call the callback immediately
    if (this.socket?.connected) {
      logger.debug('WebSocket already connected, calling callback immediately')
      try {
        callback(true)
      } catch (error) {
        console.error('📡 Error in immediate connection callback:', error)
      }
    }
  }

  offConnectionStatusChange(callback: (connected: boolean) => void): void {
    const index = this.connectionStatusCallbacks.indexOf(callback)
    if (index > -1) {
      this.connectionStatusCallbacks.splice(index, 1)
      logger.debug('Connection status callback removed, remaining callbacks:', this.connectionStatusCallbacks.length)
    }
  }

  // Generic event listeners for custom events (like price alerts)
  on(event: string, callback: (...args: any[]) => void): void {
    if (!this.socket) {
      console.warn(`📡 WebSocket not available, cannot listen to ${event}`)
      return
    }
    this.socket.on(event, callback)
  }

  off(event: string, callback?: (...args: any[]) => void): void {
    if (!this.socket) return
    if (callback) {
      // Remove specific listener
      this.socket.off(event, callback)
    } else {
      // Remove all listeners for this event
      this.socket.off(event)
    }
  }

  emit(event: string, data: any): void {
    if (!this.socket?.connected) {
      console.warn(`📡 WebSocket not connected, cannot emit ${event}`)
      return
    }
    this.socket.emit(event, data)
  }
}

export const wsService = new WebSocketService()
export default wsService
